import { useNavigate } from "@solidjs/router";
import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { createEffect, createMemo, createSignal, Show } from "solid-js";
import type { BriefEditSection } from "../../enums/brief";
import { BRIEF_STATUS } from "../../enums/brief";
import { startGovernance } from "../govern/governSession";
import { BriefDocument } from "./BriefDocument";
import { BriefEditSheet } from "./BriefEditSheet";
import { BriefPreviewHeader } from "./BriefPreviewHeader";
import { BriefQuestionResponseOverlay } from "./BriefQuestionResponseOverlay";
import { BriefQueue } from "./BriefQueue";
import { BriefSourceRail } from "./BriefSourceRail";
import { buildIssueBrief } from "./briefIssueAdapter";
import { buildMapBrief } from "./briefMapAdapter";
import { canMarkBriefReady } from "./briefReadiness";
import {
  hasSessionBriefDraft,
  registerSessionIssueBrief,
  resetSessionBriefDraft,
  sessionBriefDraft,
  sessionIssueBriefIds,
  setSessionBriefDraft,
} from "./briefSession";
import { transientMapBrief } from "./briefTransient";
import type { DeliveryBrief } from "./briefTypes";

export function BriefPreview(props: {
  data: OverviewResponse;
  websiteId: string;
  mapRequested: boolean;
  issueRequested: boolean;
  requestedIssueId: string | null;
}) {
  const navigate = useNavigate();
  const requestedIssueBrief = props.requestedIssueId
    ? buildIssueBrief(props.data, props.requestedIssueId)
    : null;
  const mapHandoff = transientMapBrief(props.websiteId);
  const mapBrief = mapHandoff ? buildMapBrief(props.data, mapHandoff) : null;
  createEffect(() => {
    if (requestedIssueBrief && props.requestedIssueId) {
      registerSessionIssueBrief(props.websiteId, props.requestedIssueId);
    }
  });
  const issueBriefs = createMemo(() => {
    const ids = [
      ...(props.requestedIssueId ? [props.requestedIssueId] : []),
      ...sessionIssueBriefIds(props.websiteId),
    ];
    return [...new Set(ids)].flatMap((issueId) => {
      const brief = buildIssueBrief(props.data, issueId);
      return brief ? [brief] : [];
    });
  });
  const baseline = createMemo(() =>
    [...issueBriefs(), mapBrief].filter(
      (brief): brief is DeliveryBrief => brief !== null,
    ),
  );
  const [selectedId, setSelectedId] = createSignal<string | null>(
    props.issueRequested
      ? (requestedIssueBrief?.id ?? null)
      : props.mapRequested && mapBrief
        ? mapBrief.id
        : (issueBriefs()[0]?.id ?? mapBrief?.id ?? null),
  );
  const [editSection, setEditSection] = createSignal<BriefEditSection | null>(
    null,
  );
  const [editTrigger, setEditTrigger] = createSignal<HTMLElement | null>(null);
  const [responseQuestionId, setResponseQuestionId] = createSignal<
    string | null
  >(null);
  const [responseTrigger, setResponseTrigger] =
    createSignal<HTMLElement | null>(null);
  const briefs = createMemo(() =>
    baseline().map(
      (brief) => sessionBriefDraft(props.websiteId, brief.id) ?? brief,
    ),
  );
  const selected = createMemo(
    () => briefs().find((brief) => brief.id === selectedId()) ?? null,
  );
  const dirty = () =>
    Boolean(
      selectedId() &&
        hasSessionBriefDraft(props.websiteId, selectedId() as string),
    );
  const updateSelected = (
    patch: Partial<DeliveryBrief>,
    resetReadyStatus = true,
  ) => {
    const current = selected();
    if (!current) return;
    const next = {
      ...current,
      ...patch,
      status:
        patch.status ??
        (resetReadyStatus && current.status === BRIEF_STATUS.READY_FOR_DELIVERY
          ? BRIEF_STATUS.DRAFT
          : current.status),
    };
    setSessionBriefDraft(props.websiteId, next);
  };
  const selectBrief = (id: string) => {
    setEditSection(null);
    setSelectedId(id);
  };
  const resetSelected = () => {
    const id = selectedId();
    if (!id) return;
    resetSessionBriefDraft(props.websiteId, id);
  };
  const recordResponse = (
    id: string,
    response: string,
    confirmationSource: string,
  ) => {
    const current = selected();
    if (!current) return;
    updateSelected(
      {
        openQuestions: current.openQuestions.map((question) =>
          question.id === id
            ? { ...question, response, confirmationSource }
            : question,
        ),
      },
      false,
    );
  };
  const reopenQuestion = (id: string) => {
    const current = selected();
    if (!current) return;
    updateSelected(
      {
        openQuestions: current.openQuestions.map((question) =>
          question.id === id
            ? { ...question, response: "", confirmationSource: "" }
            : question,
        ),
        status: BRIEF_STATUS.NEEDS_INPUT,
      },
      false,
    );
  };
  const openResponseEditor = (id: string, trigger: HTMLElement) => {
    setResponseTrigger(trigger);
    setResponseQuestionId(id);
  };
  const openEditor = (section: BriefEditSection, trigger: HTMLElement) => {
    setEditTrigger(trigger);
    setEditSection(section);
  };

  return (
    <div class="signal-route-surface brief-preview">
      <BriefPreviewHeader
        websiteId={props.websiteId}
        mapRequested={props.mapRequested}
        mapAvailable={mapBrief !== null}
        issueRequested={props.issueRequested}
        issueAvailable={requestedIssueBrief !== null}
        issueId={props.requestedIssueId}
      />

      <div class="brief-workspace-grid">
        <BriefQueue
          briefs={briefs()}
          selectedId={selectedId()}
          mapAvailable={mapBrief !== null}
          websiteId={props.websiteId}
          onSelect={selectBrief}
        />
        <Show
          when={selected()}
          keyed
          fallback={
            <section class="brief-document brief-empty-document">
              <span class="signal-mini-kicker">Limited source data</span>
              <h2>No complete delivery specification can be drafted</h2>
              <p>
                Faro found no eligible issue or accepted Map change in this
                Preview session.
              </p>
            </section>
          }
        >
          {(brief) => (
            <>
              <BriefDocument
                brief={brief}
                onEdit={openEditor}
                onRecordResponse={openResponseEditor}
                onReopenQuestion={reopenQuestion}
              />
              <BriefSourceRail
                brief={brief}
                dirty={dirty()}
                onMarkReady={() => {
                  if (canMarkBriefReady(brief)) {
                    updateSelected({ status: BRIEF_STATUS.READY_FOR_DELIVERY });
                  }
                }}
                onReturnToDraft={() =>
                  updateSelected({ status: BRIEF_STATUS.DRAFT })
                }
                onStartGovernance={() => {
                  if (!canMarkBriefReady(brief)) return;
                  const work = startGovernance(props.websiteId, brief);
                  navigate(
                    `/w/${props.websiteId}/govern?work=${encodeURIComponent(work.id)}`,
                  );
                }}
                onReset={resetSelected}
              />
            </>
          )}
        </Show>
      </div>
      <Show when={editSection()} keyed>
        {(section) => (
          <BriefEditSheet
            brief={selected() as DeliveryBrief}
            section={section}
            returnFocus={editTrigger()}
            onSave={updateSelected}
            onClose={() => setEditSection(null)}
          />
        )}
      </Show>
      <BriefQuestionResponseOverlay
        brief={selected}
        questionId={responseQuestionId}
        returnFocus={responseTrigger}
        onSave={recordResponse}
        onClose={() => setResponseQuestionId(null)}
      />
    </div>
  );
}
